import { fetchData } from 'Utils/fetch-data';
import type {
  CourseReportI,
  EmployeeReportI,
  CourseReportFilterI,
  EmployeeReportFilterI,
  EmployeeCoursesReportI,
  EmployeeCoursesReportFilterI,
  EmployeesByCourseReportFilterI,
  EmployeeCoursesReportTotalsI,
  CourseEmployeesReportTotalsI,
  EmployeesByCourseReportI,
} from '../../types';

class ReportsApi {
  endpoint: string;

  constructor() {
    this.endpoint = '/reports';
  }

  /**
   * Obtiene los datos de los empleados para el reporte por "Empleados".
   * Se pueden aplicar filtros para la consulta.
   */
  async readEmployees(filters: EmployeeReportFilterI) {
    const response = await fetchData<EmployeeReportI[]>(`${this.endpoint}/employees`, {
      method: 'GET',
      params: filters,
    });
    return response;
  }

  /**
   * Exporta los datos de los empleados en un archivo Excel. Se utiliza
   * para el reporte por "Empleados". Respeta el filtro aplicado en la consulta.
   */
  async exportEmployees(filters: EmployeeReportFilterI) {
    const response = await fetchData<Blob>(`${this.endpoint}/employees/export`, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
    return response;
  }

  /**
   * Obtiene los datos de los cursos que tiene un empleado en específico.
   * Se pueden aplicar filtros para la consulta.
   */
  async readCoursesByEmployee(id: string, filters: EmployeeCoursesReportFilterI) {
    const response = await fetchData<EmployeeCoursesReportI[]>(`${this.endpoint}/employees/${id}/courses`, {
      method: 'GET',
      params: filters,
    });
    return response;
  }

  /**
   * Obtiene los datos totales de los cursos que tiene un empleado en específico.
   */
  async readCoursesByEmployeeTotals(id: string) {
    const response = await fetchData<EmployeeCoursesReportTotalsI>(`${this.endpoint}/employees/${id}/courses/totals`, {
      method: 'GET',
    });
    return response;
  }

  /**
   * Exporta los datos de los cursos que tiene un empleado en específico en un archivo Excel.
   * Se utiliza para el reporte por "Cursos". Respeta el filtro aplicado en la consulta.
   */
  async exportCoursesByEmployee(id: string, filters: EmployeeCoursesReportFilterI) {
    const response = await fetchData<Blob>(`${this.endpoint}/employees/${id}/courses/export`, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
    return response;
  }

  /**
   * Obtiene los datos de los empleados para el reporte por "Cursos".
   * Se pueden aplicar filtros para la consulta.
   */
  async readCourses(filters: CourseReportFilterI) {
    const response = await fetchData<CourseReportI[]>(`${this.endpoint}/courses`, {
      method: 'GET',
      params: filters,
    });
    return response;
  }

  /**
   * Exporta los datos de los cursos en un archivo Excel. Se utiliza
   * para el reporte por "Cursos". Respeta el filtro aplicado en la consulta.
   * @param filters - Los filtros para la consulta.
   * @returns El archivo Excel con los datos de los cursos.
   */
  async exportCourses(filters: CourseReportFilterI) {
    const response = await fetchData<Blob>(`${this.endpoint}/courses/export`, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
    return response;
  }

  /**
   * Obtiene los datos de los empleados de un curso en específico.
   * Se pueden aplicar filtros para la consulta.
   * @param id - El ID del curso.
   * @param filters - Los filtros para la consulta.
   * @returns Los datos de los empleados de un curso en específico.
   */
  async readEmployeesByCourse(id: string, filters: EmployeesByCourseReportFilterI) {
    const response = await fetchData<EmployeesByCourseReportI[]>(`${this.endpoint}/courses/${id}/employees`, {
      method: 'GET',
      params: filters,
    });
    return response;
  }

  /**
   * Obtiene los datos totales de los empleados de un curso en específico.
   * @param id - El ID del curso.
   * @returns Los datos totales de los empleados de un curso en específico.
   */
  async readEmployeesByCourseTotals(id: string) {
    const response = await fetchData<CourseEmployeesReportTotalsI>(`${this.endpoint}/courses/${id}/employees/totals`, {
      method: 'GET',
    });
    return response;
  }

  /**
   * Exporta los datos de los empleados de un curso en específico en un archivo Excel.
   * Se utiliza para el reporte por "Empleados por Curso". Respeta el filtro aplicado en la consulta.
   */
  async exportEmployeesByCourse(id: string, filters: EmployeesByCourseReportFilterI) {
    const response = await fetchData<Blob>(`${this.endpoint}/courses/${id}/employees/export`, {
      method: 'GET',
      params: filters,
      responseType: 'blob',
    });
    return response;
  }
}

export const reportsApi = new ReportsApi();
